import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { type DashboardRange } from '@/features/dashboard/hooks';
import { buildLocalQueryResult } from '@/ui/composites/grid/localGridUtils';
import { createRegisterInterest } from './landingApi';
import { buildLandingChartData } from './landingCharts';
import {
  buildStatusCards,
  metricValueCards,
  statusTrendSeries,
  suiteExecutionsRows,
  suitesTableRows,
} from './landingData';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteExecutionDto, SuiteListItemDto } from '@flowqa/shared';

type LandingChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';
type LandingCarouselDirection = 'next' | 'prev';
type LandingSubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

type LandingFormViewModel = {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  role: string;
  companySize: string;
  submitStatus: LandingSubmitStatus;
  submitStatusForBanner: 'idle' | 'success' | 'error';
  onFirstNameChange: (next: string) => void;
  onLastNameChange: (next: string) => void;
  onCompanyNameChange: (next: string) => void;
  onEmailChange: (next: string) => void;
  onRoleChange: (next: string) => void;
  onCompanySizeChange: (next: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
};

type LandingChartViewModel = {
  rangeTab: DashboardRange;
  setRangeTab: (value: DashboardRange) => void;
  chartTab: LandingChartTab;
  setChartTab: (value: LandingChartTab) => void;
  statusCards: ReturnType<typeof buildStatusCards>;
  metricCardsForRange: (typeof metricValueCards)[DashboardRange];
  statusTrendChartData: ReturnType<typeof buildLandingChartData>['statusTrendChartData'];
  passRateChartData: ReturnType<typeof buildLandingChartData>['passRateChartData'];
  frequencyChartData: ReturnType<typeof buildLandingChartData>['frequencyChartData'];
  durationChartData: ReturnType<typeof buildLandingChartData>['durationChartData'];
};

type LandingCarouselsViewModel = {
  carouselIndex: number;
  carouselDirection: LandingCarouselDirection;
  carouselSlides: Array<{ id: string }>;
  onPrevSlide: () => void;
  onNextSlide: () => void;
  setCarouselIndex: (value: number) => void;
  tablesCarouselIndex: number;
  tablesCarouselDirection: LandingCarouselDirection;
  tablesCarouselSlides: Array<{ id: string }>;
  onPrevTableSlide: () => void;
  onNextTableSlide: () => void;
  setTablesCarouselIndex: (value: number) => void;
};

type LandingTableQueriesViewModel = {
  suiteExecutionsQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteExecutionDto>>;
  suitesQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
};

type LandingViewModel = {
  form: LandingFormViewModel;
  chart: LandingChartViewModel;
  carousels: LandingCarouselsViewModel;
  tableQueries: LandingTableQueriesViewModel;
  staggerReady: boolean;
};

const carouselSlides: Array<{ id: string }> = [{ id: 'metrics' }, { id: 'charts' }];
const tablesCarouselSlides: Array<{ id: string }> = [{ id: 'suite-executions' }, { id: 'suites' }];

export const useLandingViewModel = (): LandingViewModel => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [chartTab, setChartTab] = useState<LandingChartTab>('status');
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [tablesCarouselIndex, setTablesCarouselIndex] = useState(0);
  const [carouselDirection, setCarouselDirection] = useState<LandingCarouselDirection>('next');
  const [tablesCarouselDirection, setTablesCarouselDirection] =
    useState<LandingCarouselDirection>('next');
  const [staggerReady, setStaggerReady] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  const [submitStatus, setSubmitStatus] = useState<LandingSubmitStatus>('idle');
  const [rangeTab, setRangeTab] = useState<DashboardRange>('14d');

  const statusCards = useMemo(() => buildStatusCards(rangeTab), [rangeTab]);
  const metricCardsForRange = useMemo(() => metricValueCards[rangeTab], [rangeTab]);
  const { statusTrendChartData, passRateChartData, frequencyChartData, durationChartData } =
    useMemo(() => buildLandingChartData(statusTrendSeries), []);

  const suiteExecutionsQuery = async (
    input: GridQueryInput,
  ): Promise<QueryResult<SuiteExecutionDto>> =>
    await Promise.resolve(buildLocalQueryResult(suiteExecutionsRows, input));
  const suitesQuery = async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> =>
    await Promise.resolve(buildLocalQueryResult(suitesTableRows, input));

  const totalSlides = carouselSlides.length;
  const totalTableSlides = tablesCarouselSlides.length;
  const handlePrevSlide = (): void => {
    setCarouselDirection('prev');
    setCarouselIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };
  const handleNextSlide = (): void => {
    setCarouselDirection('next');
    setCarouselIndex((prev) => (prev + 1) % totalSlides);
  };
  const handlePrevTableSlide = (): void => {
    setTablesCarouselDirection('prev');
    setTablesCarouselIndex((prev) => (prev - 1 + totalTableSlides) % totalTableSlides);
  };
  const handleNextTableSlide = (): void => {
    setTablesCarouselDirection('next');
    setTablesCarouselIndex((prev) => (prev + 1) % totalTableSlides);
  };
  const resetSubmitStatus = (): void => {
    if (submitStatus === 'success' || submitStatus === 'error') {
      setSubmitStatus('idle');
    }
  };
  const handleFirstNameChange = (next: string): void => {
    resetSubmitStatus();
    setFirstName(next);
  };
  const handleLastNameChange = (next: string): void => {
    resetSubmitStatus();
    setLastName(next);
  };
  const handleCompanyNameChange = (next: string): void => {
    resetSubmitStatus();
    setCompanyName(next);
  };
  const handleEmailChange = (next: string): void => {
    resetSubmitStatus();
    setEmail(next);
  };
  const handleRoleChange = (next: string): void => {
    resetSubmitStatus();
    setRole(next);
  };
  const handleCompanySizeChange = (next: string): void => {
    resetSubmitStatus();
    setCompanySize(next);
  };
  const submitStatusForBanner: 'idle' | 'success' | 'error' =
    submitStatus === 'submitting' ? 'idle' : submitStatus;
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (submitStatus === 'submitting') return;
    if (!email.trim() || !firstName.trim() || !lastName.trim()) return;
    setSubmitStatus('submitting');
    try {
      await createRegisterInterest({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        companyName: companyName.trim() ? companyName.trim() : null,
        role: role.trim() ? role.trim() : null,
        companySize: companySize.trim() ? companySize.trim() : null,
      });
      setSubmitStatus('success');
      setFirstName('');
      setLastName('');
      setCompanyName('');
      setEmail('');
      setRole('');
      setCompanySize('');
    } catch {
      setSubmitStatus('error');
    }
  };

  useEffect(() => {
    if (staggerReady) return;
    const id = window.requestAnimationFrame(() => {
      setStaggerReady(true);
    });
    return () => {
      window.cancelAnimationFrame(id);
    };
  }, [staggerReady]);

  return {
    form: {
      firstName,
      lastName,
      companyName,
      email,
      role,
      companySize,
      submitStatus,
      submitStatusForBanner,
      onFirstNameChange: handleFirstNameChange,
      onLastNameChange: handleLastNameChange,
      onCompanyNameChange: handleCompanyNameChange,
      onEmailChange: handleEmailChange,
      onRoleChange: handleRoleChange,
      onCompanySizeChange: handleCompanySizeChange,
      onSubmit: handleSubmit,
    },
    chart: {
      rangeTab,
      setRangeTab,
      chartTab,
      setChartTab,
      statusCards,
      metricCardsForRange,
      statusTrendChartData,
      passRateChartData,
      frequencyChartData,
      durationChartData,
    },
    carousels: {
      carouselIndex,
      carouselDirection,
      carouselSlides,
      onPrevSlide: handlePrevSlide,
      onNextSlide: handleNextSlide,
      setCarouselIndex,
      tablesCarouselIndex,
      tablesCarouselDirection,
      tablesCarouselSlides,
      onPrevTableSlide: handlePrevTableSlide,
      onNextTableSlide: handleNextTableSlide,
      setTablesCarouselIndex,
    },
    tableQueries: {
      suiteExecutionsQuery,
      suitesQuery,
    },
    staggerReady,
  };
};
